import * as React from 'react';
import { Check, MapPin, Pencil, Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { extractAddressComplementParts } from '@klareospace/pharos-lib-front-utils';
import { cn } from '@klareospace/pharos-lib-web-utils';
import type { Customer, CustomerAddress } from '@klareospace/pharos-lib-types';

import { buildAddressEntryFromCustomerAddress } from '&/features/customers';
import type { AddressBookEntryInput } from '&/features/customers';
import { AddressComplementParts } from '&/features/orders/components';

import { formatRouteAddress, resolveDeliveryContact } from '../../utils';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface DeliveryAddressPickerProps {
  addresses: Customer['addresses'];
  customer: Customer;
  onAddNew: () => void;
  onCancel: () => void;
  onEdit: (index: number) => void;
  onSelect: (index: number) => void;
  /**
   * The live form delivery address entry — shown on the selected row instead of rebuilding it
   * from the stored address, so a form-only edit (no `customers:manage`, never PATCHed back) is
   * what the user sees ticked, not the stale stored value.
   */
  selectedEntry: AddressBookEntryInput | null;
  /** Index of the entry currently used as the delivery address (`deliveryAddress.sourceIndex`), undefined when none matches. */
  selectedIndex: number | undefined;
}

interface AddressPickerRowProps {
  address: CustomerAddress;
  customer: Customer;
  index: number;
  isSelected: boolean;
  onEdit: (index: number) => void;
  onSelect: (index: number) => void;
  /** The live form entry to display instead of the stored address — only passed for the selected row. */
  overrideEntry: AddressBookEntryInput | null;
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Inline list of a customer's saved addresses, opened from the Delivery pencil — mirrors
 * `PickupBlock`'s inline site edit. Each row is selectable (sets the delivery address) and
 * editable (opens the shared `AddressDialog`); a dashed row underneath starts a brand-new address.
 * No delete action here — address-book management stays in the customer drawer.
 */
export function DeliveryAddressPicker({
  addresses,
  customer,
  onAddNew,
  onCancel,
  onEdit,
  onSelect,
  selectedEntry,
  selectedIndex,
}: DeliveryAddressPickerProps): React.JSX.Element {
  const { t: tCommon } = useTranslation('common');
  const { t } = useTranslation('order-creation');

  return (
    <div
      aria-label={t('sections.routeInformation.delivery.chooseAddress')}
      className="space-y-2 rounded-md border p-2"
      role="group"
    >
      {addresses.map((address, index) => (
        <AddressPickerRow
          address={address}
          customer={customer}
          index={index}
          isSelected={index === selectedIndex}
          key={index}
          onEdit={onEdit}
          onSelect={onSelect}
          overrideEntry={index === selectedIndex ? selectedEntry : null}
        />
      ))}

      <button
        className="flex w-full items-center gap-2 rounded-md border border-dashed p-2 text-sm text-muted-foreground hover:bg-muted/50"
        onClick={onAddNew}
        type="button"
      >
        <Plus aria-hidden="true" className="h-3.5 w-3.5" />
        {t('sections.routeInformation.delivery.newAddress')}
      </button>

      <button
        className="text-sm font-medium text-muted-foreground hover:text-foreground"
        onClick={onCancel}
        type="button"
      >
        {tCommon('actions.cancel')}
      </button>
    </div>
  );
}

// ─── Helpers (private) ──────────────────────────────────────────────────────────

/** One selectable, editable saved-address row. */
function AddressPickerRow({
  address,
  customer,
  index,
  isSelected,
  onEdit,
  onSelect,
  overrideEntry,
}: AddressPickerRowProps): React.JSX.Element {
  const { t } = useTranslation('order-creation');
  const entry = overrideEntry ?? buildAddressEntryFromCustomerAddress(address, index, customer);
  const formattedAddress = formatRouteAddress(entry.address);
  const complementParts = extractAddressComplementParts(entry.address);
  const contact = resolveDeliveryContact(entry, customer);

  return (
    <div
      className={cn(
        'flex items-start justify-between gap-2 rounded-md border p-2',
        isSelected ? 'border-primary/30 bg-primary/5' : 'border-transparent hover:bg-muted/50',
      )}
    >
      <button
        aria-label={t('sections.routeInformation.delivery.selectAddress', { address: formattedAddress })}
        className="flex min-w-0 flex-1 items-start gap-2 text-start"
        onClick={(): void => onSelect(index)}
        type="button"
      >
        <span className="mt-0.5 shrink-0 text-primary">
          {isSelected ? (
            <Check aria-hidden="true" className="h-3.5 w-3.5" />
          ) : (
            <MapPin aria-hidden="true" className="h-3.5 w-3.5" />
          )}
        </span>
        <span className="min-w-0 space-y-0.5">
          <p className="truncate text-sm font-medium text-foreground">{formattedAddress}</p>
          <AddressComplementParts parts={complementParts} />
          {contact.name && <p className="truncate text-sm text-foreground">{contact.name}</p>}
          {contact.phone && <p className="truncate text-sm text-muted-foreground">{contact.phone}</p>}
        </span>
      </button>
      <button
        aria-label={t('sections.routeInformation.delivery.editAddressEntry', { address: formattedAddress })}
        className="shrink-0 cursor-pointer rounded-md p-1 text-primary hover:bg-muted hover:text-primary/80"
        onClick={(): void => onEdit(index)}
        type="button"
      >
        <Pencil aria-hidden="true" className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
